import logo from 'assets/header-prevencor-xx.png';
import md5 from 'md5';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import fetchImageBase64 from 'utils/helpers';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const LEADS_13 = [
  'MC5m',
  'D1m',
  'D2m',
  'D3m',
  'AVRm',
  'AVLm',
  'AVFm',
  'V1m',
  'V2m',
  'V3m',
  'V4m',
  'V5m',
  'V6m',
];
const LEADS_EXTRA = ['V3R', 'V4R', 'V5R'];
const ALL_LEADS = [...LEADS_13, ...LEADS_EXTRA];

function buildListContent(title, items, { keyName = 'DESCRICAO', fallbacks = [] } = {}) {
  if (!items?.length) return [];
  const allKeys = [keyName, ...fallbacks];
  const lines = items
    .map((item) => {
      const text = allKeys.reduce((acc, k) => acc || String(item[k] ?? ''), '');
      return text.trim();
    })
    .filter(Boolean);
  if (!lines.length) return [];
  return [
    {
      text: `${title}:`,
      bold: true,
      decoration: 'underline',
      margin: [0, 6, 0, 2],
    },
    ...lines.map((line) => ({ text: line, bold: true, margin: [10, 0, 0, 1] })),
  ];
}

function buildAltECGList(alteracoesECG, leadsToShow) {
  const row = Array.isArray(alteracoesECG) ? alteracoesECG[0] || {} : {};

  const items = leadsToShow.map((lead) => {
    const a = String(row[`${lead}A`] ?? '');
    const e = String(row[`${lead}E`] ?? '');
    const d = String(row[`${lead}D`] ?? '');
    return {
      columns: [
        { text: `${lead}:`, bold: true, width: 42 },
        {
          stack: [
            {
              text: [{ text: 'ANTES...............: ', bold: true }, a],
            },
            {
              text: [{ text: 'NO ESFORÇO...: ', bold: true }, e],
            },
            {
              text: [{ text: 'APÓS.................: ', bold: true }, d],
            },
          ],
          width: '*',
        },
      ],
      columnGap: 4,
      margin: [0, 2, 0, 2],
    };
  });

  return [
    {
      text: 'Alterações Eletrocardiográficas:',
      bold: true,
      decoration: 'underline',
      margin: [0, 6, 0, 4],
      pageBreak: 'before',
    },
    ...items,
  ];
}

export async function generateErgoPdf({ ergo }) {
  const logoBase64 = await fetchImageBase64(logo);

  const ecgRow = Array.isArray(ergo?.alteracoesECG) ? ergo.alteracoesECG[0] || {} : {};
  const hasExtraLeads = LEADS_EXTRA.some(
    (lead) => ecgRow[`${lead}A`] || ecgRow[`${lead}E`] || ecgRow[`${lead}D`]
  );
  const canais = hasExtraLeads ? 16 : 13;
  const leadsToShow = hasExtraLeads ? ALL_LEADS : LEADS_13;

  const hashAssinatura = md5(JSON.stringify(ergo));

  const content = [
    {
      text: 'Teste Ergométrico em Esteira Rolante',
      decoration: 'underline',
      bold: true,
      alignment: 'center',
      margin: [0, 0, 0, 2],
    },
    {
      text: `Monitorização em ${canais} Canais`,
      decoration: 'underline',
      bold: true,
      alignment: 'center',
      margin: [0, 0, 0, 10],
    },

    // Dados do paciente
    {
      text: [{ text: 'Paciente: ', bold: true }, `${ergo.CODPAC} ${ergo.NOMEPAC}`],
      bold: true,
    },
    {
      columns: [
        {
          text: [
            { text: 'Sexo: ', bold: true },
            ergo.SEXOPAC || '-',
            { text: '   Idade: ', bold: true },
            `${ergo.idadePAC} anos`,
          ],
          width: '*',
        },
        {
          text: [{ text: 'Data do Laudo: ', bold: true }, ergo.DATAERGO || '-'],
          width: 'auto',
          alignment: 'right',
        },
      ],
      margin: [0, 2, 0, 0],
    },
    {
      columns: [
        {
          text: [{ text: 'Médico que Realizou: ', bold: true }, `Dr.(ª): ${ergo.Realizou || '-'}`],
          width: '*',
        },
        {
          text: String(ergo.CODEXAME || ''),
          bold: true,
          width: 'auto',
          alignment: 'right',
        },
      ],
      margin: [0, 2, 0, 0],
    },
    {
      text: [{ text: 'Médico que Solicitou: ', bold: true }, `Dr.(ª): ${ergo.Solicitou || '-'}`],
      margin: [0, 2, 0, 0],
    },
    {
      text: [{ text: 'Convênio: ', bold: true }, ergo.SIGLACONV || '-'],
      margin: [0, 2, 0, 6],
    },

    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }],
      margin: [0, 0, 0, 6],
    },

    // Dados Clínicos
    ...buildListContent('Indicações do Teste', ergo.indicacoes),
    ...buildListContent('Queixas', ergo.queixas, {
      keyName: 'FRASE',
      fallbacks: ['DESCRICAO'],
    }),
    ...buildListContent('Medicamentos', ergo.medicamentos, {
      keyName: 'FRASE',
      fallbacks: ['DESCRICAO'],
    }),
    ...buildListContent('Fatores de Risco', ergo.fatoresRisco),

    // Avaliações
    ...buildListContent('Atividade Física', ergo.atividadesFisica),

    ...(ergo.EXAMEFISICO
      ? [
          {
            text: 'Exame Físico:',
            bold: true,
            decoration: 'underline',
            margin: [0, 6, 0, 2],
          },
          { text: ergo.EXAMEFISICO, bold: true, margin: [10, 0, 0, 1] },
        ]
      : []),

    ...buildListContent('Eletrocardiograma Basal', ergo.ecgBasal, {
      keyName: 'FRASE',
      fallbacks: ['DESCRICAO'],
    }),

    // Alterações ECG — lista por derivação
    ...buildAltECGList(ergo.alteracoesECG, leadsToShow),

    // PAGE 3 — Arritmias / Comportamento PA
    { text: '', pageBreak: 'before' },
    ...buildListContent('Arritmias', ergo.arritmias),
    ...buildListContent('Comportamento da PA', ergo.comportamentoPA),

    // Comentários — Índice Cronotrópico como primeiro item
    {
      text: 'Comentários:',
      bold: true,
      decoration: 'underline',
      margin: [0, 6, 0, 2],
    },
    ...(ergo.PADEITADO
      ? [
          {
            text: `Índice Cronotrópico: ${ergo.PADEITADO}%`,
            margin: [10, 0, 0, 1],
          },
        ]
      : []),
    ...(ergo.comentarios || [])
      .map((c) => {
        const t = c.DESCRICAO || '';
        return t ? { text: t, bold: true, margin: [10, 0, 0, 1] } : null;
      })
      .filter(Boolean),

    // Observações
    {
      text: 'Observações:',
      bold: true,
      decoration: 'underline',
      margin: [0, 6, 0, 2],
    },
    ...(ergo.OBS ? [{ text: String(ergo.OBS), margin: [10, 0, 0, 1] }] : []),

    // Conclusões — Escore de Duke como primeiro item
    {
      text: 'Conclusões:',
      bold: true,
      decoration: 'underline',
      margin: [0, 6, 0, 2],
    },
    ...(ergo.PAHIPER
      ? [
          {
            text: `Escore de Duke: ${ergo.PAHIPER}`,
            margin: [10, 0, 0, 1],
          },
        ]
      : []),
    ...(ergo.conclusoes || [])
      .map((c) => {
        const t = c.DESCRICAO || '';
        return t ? { text: t, bold: true, margin: [10, 0, 0, 1] } : null;
      })
      .filter(Boolean),

    // Assinatura eletrônica
    {
      text: 'Conferido e assinado eletronicamente por:',
      italics: true,
      alignment: 'center',
      margin: [0, 20, 0, 3],
    },
    {
      text: `Dr(a).: ${ergo.Realizou || '-'}`,
      bold: true,
      alignment: 'center',
      margin: [0, 0, 0, 3],
    },
    {
      text: `Assinatura Digital: ${hashAssinatura}`,
      bold: true,
      italics: true,
      alignment: 'center',
      fontSize: 9,
      margin: [0, 0, 0, 12],
    },

    // Rodapé
    {
      text: 'Obs - O Resultado do presente laudo não configura necessariamente a presença ou ausência de doença, devendo obrigatoriamente ser correlacionado com demais dados clínicos e exames complementares pertinentes ao caso.',
      fontSize: 8,
      italics: true,
      margin: [0, 12, 0, 4],
      alignment: 'center',
    },
  ];

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 90, 40, 68],
    header: function () {
      return {
        stack: [
          {
            image: logoBase64,
            width: 500,
            alignment: 'center',
            margin: [0, 6, 0, 2],
          },
          {
            text: String(ergo.CODEXAME || ''),
            bold: true,
            alignment: 'right',
            fontSize: 10,
            margin: [40, 0, 40, 0],
          },
        ],
      };
    },
    footer: function (currentPage, pageCount) {
      return [
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: 515,
              y2: 0,
              lineWidth: 0.5,
            },
          ],
          margin: [40, 2, 40, 0],
        },
        {
          text: 'Rua Claudio Manuel da Costa, 56 - Fone (14) 3402-2700/Fax: (14) 3402-2705 - Jd Maria Isabel - CEP 17.515-460 - Marília/SP',
          alignment: 'center',
          fontSize: 7,
          margin: [40, 2, 40, 0],
        },
        {
          text: 'prevencor@unimedmarilia.com.br',
          alignment: 'center',
          fontSize: 7,
          margin: [40, 0, 40, 0],
        },
        {
          text: `Pág: ${currentPage} de ${pageCount}`,
          alignment: 'center',
          fontSize: 8,
          bold: true,
          margin: [40, 1, 40, 0],
        },
      ];
    },
    content,
    styles: {
      title: { fontSize: 14, bold: true },
    },
    defaultStyle: { fontSize: 10 },
  };

  pdfMake.createPdf(docDefinition).open();
}
